/**
 * Accepting a staff invitation.
 *
 * This is where the invite link (`/accept-invitation?token=…`) lands. One act, done here rather
 * than spread across an inbox round-trip: give your name, choose a password, and you are in. The
 * server creates the account, sets the password and signs you in, so this screen ends in the app
 * rather than at a sign-in form asking you to retype what you just chose.
 *
 * A link with no token is a broken or truncated URL, not a form to fill in — it says so plainly and
 * points at sign-in, because a name-and-password box with nothing to attach them to would only fail
 * on submit.
 *
 * The confirm field is a typo check done here, beside the keyboard, for the same reason the
 * password screen keeps it client-side: it is not a security control, and the answer is needed
 * while the person's hands are still on the keys.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, Card, TextField } from "@powwow/ui";
import { acceptInvitation, fetchMe, describeError } from "../api.js";

/** Matches `passwordSchema` in @powwow/validation — length, and nothing else. */
const MINIMUM = 12;

export function AcceptInvitationScreen() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < MINIMUM;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || password.length < MINIMUM || password !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation({ token, name: name.trim(), password });
      const me = await fetchMe();
      // A platform admin with no community of their own belongs in the console; everyone else lands
      // in the workspace, which picks the community. Same rule the magic-link sign-in follows.
      navigate(
        me.authenticated && me.platformAdmin && me.communities.length === 0
          ? "/admin/communities"
          : "/",
        { replace: true },
      );
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="grid min-h-dvh place-items-center px-4 py-10">
        <Card className="w-full max-w-md p-6">
          <h1 className="text-title font-black">That invitation link is incomplete</h1>
          <p className="mt-2 text-muted">
            The link seems to be missing its token — often a sign it was cut off when copied out of
            an email. Open it straight from the invitation, or ask whoever invited you to send a new
            one.
          </p>
          <Button className="mt-5 w-full" onClick={() => navigate("/sign-in", { replace: true })}>
            Go to sign in
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4 py-10">
      <Card className="w-full max-w-md p-6">
        <h1 className="text-title font-black">Accept your invitation</h1>
        <p className="mt-1 text-muted">
          Give your name and choose a password. That is your account — you will sign in with it from
          now on.
        </p>

        {error && (
          <Callout label="Problem" tone="danger" className="mt-4">
            {error}
          </Callout>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          <TextField
            label="Your name"
            value={name}
            autoComplete="name"
            required
            hint="How you will appear to the rest of the team."
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            autoComplete="new-password"
            required
            hint={`At least ${MINIMUM} characters. A short phrase you will remember beats a jumble you will not.`}
            {...(tooShort ? { error: `Use at least ${MINIMUM} characters` } : {})}
            onChange={(event) => setPassword(event.target.value)}
          />
          <TextField
            label="Confirm password"
            type="password"
            value={confirm}
            autoComplete="new-password"
            required
            {...(mismatch ? { error: "These do not match" } : {})}
            onChange={(event) => setConfirm(event.target.value)}
          />
          <Button
            type="submit"
            busy={busy}
            disabled={!name.trim() || password.length < MINIMUM || password !== confirm}
            className="w-full"
          >
            {busy ? "Joining…" : "Join and sign in"}
          </Button>
        </form>
      </Card>
    </main>
  );
}
